import * as THREE from 'three';
import { HUD } from '../config/tuning';
import { AIRFRAME_SCOUT } from '../config/airframes';
import { stallSpeedMs } from '../flight/Aerodynamics';
import { BODY_FORWARD, BODY_RIGHT, BODY_UP } from '../flight/FlightState';
import type { NoseCamera } from '../render/Camera';
import type { Hud } from '../hud/Hud';
import {
  RwrState,
  TagKind,
  createHudFrame,
  type BearingTag,
  type MissileWarning,
  type RwrContact,
} from '../hud/HudFrame';

/**
 * The HUD tuning page (`?scene=hudtest`, an M2 implementation note).
 *
 * Tuning a HUD while flying is miserable — the values you need to inspect are exactly the ones
 * that will not hold still. This page drives the *real* HUD and the *real* camera from sliders,
 * so every element can be pushed to its limits (90° of bank, 60° nose-down, off-scale g, a
 * dropped link, full jamming) and held there while the geometry is checked.
 *
 * It shares no code path with the flight scene beyond the two objects it is given, which is the
 * point: what is tuned here is what ships.
 */

const DEG_TO_RAD = Math.PI / 180;

interface SliderSpec {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
}

const SLIDERS = {
  pitch: { label: 'PITCH', min: -90, max: 90, step: 1, value: 8, unit: '°' },
  bank: { label: 'BANK', min: -180, max: 180, step: 1, value: 30, unit: '°' },
  heading: { label: 'HEADING', min: 0, max: 359, step: 1, value: 42, unit: '°' },
  speed: { label: 'AIRSPEED', min: 0, max: 140, step: 1, value: 78, unit: ' M/S' },
  altitude: { label: 'ALTITUDE', min: 0, max: 4000, step: 5, value: 420, unit: ' M' },
  loadFactor: { label: 'LOAD FACTOR', min: -3, max: 8, step: 0.1, value: 1, unit: ' G' },
  alpha: { label: 'ALPHA', min: -20, max: 25, step: 0.5, value: 4, unit: '°' },
  beta: { label: 'SIDESLIP', min: -20, max: 20, step: 0.5, value: 0, unit: '°' },
  throttle: { label: 'THROTTLE', min: 0, max: 1, step: 0.01, value: 0.55 },
  endurance: { label: 'ENDURANCE', min: 0, max: 1200, step: 5, value: 640, unit: ' S' },
  controlLock: { label: 'START HOLD', min: 0, max: 6, step: 0.1, value: 0, unit: ' S' },
  link: { label: 'LINK', min: 0, max: 1, step: 0.01, value: 1 },
  // Only drawn when auto-exposure is switched off; see hud/elements/Exposure.ts.
  gain: { label: 'GAIN', min: -3, max: 3, step: 0.1, value: 0, unit: ' EV' },
  jamming: { label: 'JAMMING', min: 0, max: 1, step: 0.01, value: 0 },
  chaff: { label: 'CHAFF', min: 0, max: 30, step: 1, value: 12 },
  flares: { label: 'FLARES', min: 0, max: 30, step: 1, value: 16 },
  look: { label: 'LOOK YAW', min: -60, max: 60, step: 1, value: 0, unit: '°' },
  zoom: { label: 'ZOOM', min: 0, max: 1, step: 0.01, value: 0 },
} satisfies Record<string, SliderSpec>;

type SliderKey = keyof typeof SLIDERS;

const PANEL_STYLE = `
  position: fixed; top: 12px; left: 12px; z-index: 20;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.82);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 12px; width: 260px; max-height: calc(100vh - 24px); overflow-y: auto;
`;

export function startHudTestScene(hud: Hud, camera: NoseCamera, hudCanvas: HTMLCanvasElement): void {
  const values: Record<SliderKey, number> = {} as Record<SliderKey, number>;
  for (const key of Object.keys(SLIDERS) as SliderKey[]) {
    values[key] = SLIDERS[key].value;
  }

  const toggles = {
    rwr: true,
    missiles: true,
    tags: true,
    designation: true,
    gainReadout: false,
    highContrast: hud.isHighContrast,
  };

  buildPanel(values, toggles, hud);

  const frame = createHudFrame();
  const tags: BearingTag[] = [
    makeTag('K-2', 'FIRE POINT', 20, TagKind.Threat),
    makeTag('W-1', 'INGRESS', -75, TagKind.Waypoint),
    makeTag('T-7', 'BRIDGE', 150, TagKind.Target),
    makeTag('K-9', 'SAM', -160, TagKind.Threat),
  ];
  const contacts: RwrContact[] = [
    { relativeBearingRad: 25 * DEG_TO_RAD, state: RwrState.Search, label: 'SA' },
    { relativeBearingRad: -110 * DEG_TO_RAD, state: RwrState.Track, label: 'TR' },
    { relativeBearingRad: 165 * DEG_TO_RAD, state: RwrState.Launch, label: 'ML' },
  ];
  const missiles: MissileWarning[] = [
    { relativeBearingRad: 140 * DEG_TO_RAD, closureMs: 320 },
  ];

  const aircraftPosition = new THREE.Vector3(0, 0, 0);
  const designation = new THREE.Vector3();
  const orientation = new THREE.Quaternion();
  const yawQuat = new THREE.Quaternion();
  const pitchQuat = new THREE.Quaternion();
  const rollQuat = new THREE.Quaternion();
  const velocity = new THREE.Vector3();
  const alphaQuat = new THREE.Quaternion();
  const betaQuat = new THREE.Quaternion();

  const startMs = performance.now();

  const drawFrame = (): void => {
    requestAnimationFrame(drawFrame);

    const widthPx = hudCanvas.clientWidth;
    const heightPx = hudCanvas.clientHeight;
    hud.syncToCanvasSize();
    camera.resize(widthPx, heightPx);

    // Build the airframe attitude from the sliders, then hand it to the real camera rig so the
    // 4° install tilt, the look cone and the zoom all go through production code.
    const headingRad = values.heading * DEG_TO_RAD;
    yawQuat.setFromAxisAngle(BODY_UP, -headingRad);
    pitchQuat.setFromAxisAngle(BODY_RIGHT, values.pitch * DEG_TO_RAD);
    rollQuat.setFromAxisAngle(BODY_FORWARD, values.bank * DEG_TO_RAD);
    orientation.copy(yawQuat).multiply(pitchQuat).multiply(rollQuat);

    camera.lookYawRad = values.look * DEG_TO_RAD;
    camera.zoom = values.zoom;
    camera.updateView(0, INACTIVE_LOOK, false);
    camera.lookYawRad = values.look * DEG_TO_RAD; // updateView decays it; the slider is absolute
    camera.zoom = values.zoom;
    camera.setOrientation(orientation);

    // Velocity sits α below and β beside the nose, which is what puts the flight path marker
    // where it belongs relative to the reticle.
    alphaQuat.setFromAxisAngle(BODY_RIGHT, -values.alpha * DEG_TO_RAD);
    betaQuat.setFromAxisAngle(BODY_UP, -values.beta * DEG_TO_RAD);
    velocity.copy(BODY_FORWARD).applyQuaternion(alphaQuat).applyQuaternion(betaQuat);
    velocity.applyQuaternion(orientation);

    frame.pitchRad = values.pitch * DEG_TO_RAD;
    frame.bankRad = values.bank * DEG_TO_RAD;
    frame.headingRad = headingRad;
    frame.airspeedMs = values.speed;
    frame.altitudeAglM = values.altitude;
    frame.loadFactor = values.loadFactor;
    frame.stallSpeedMs = stallSpeedMs(
      AIRFRAME_SCOUT,
      1.225,
      Math.max(1, Math.abs(values.loadFactor)),
    );
    frame.vneMs = AIRFRAME_SCOUT.vneMs;
    frame.throttle = values.throttle;
    frame.enduranceS = values.endurance;
    frame.controlLockS = values.controlLock;
    frame.linkQuality = values.link;
    frame.exposureStops = toggles.gainReadout ? values.gain : null;
    frame.jamming = values.jamming;
    frame.chaffRemaining = values.chaff;
    frame.flaresRemaining = values.flares;
    frame.velocityDirection.copy(velocity);
    frame.velocityValid = true;
    frame.bearingTags = toggles.tags ? tags : EMPTY_TAGS;
    frame.rwrContacts = toggles.rwr ? contacts : EMPTY_CONTACTS;
    frame.missileWarnings = toggles.missiles ? missiles : EMPTY_MISSILES;
    frame.simTimeS = (performance.now() - startMs) / 1000;

    if (toggles.designation) {
      // 900 m ahead along the boresight, dropped 60 m — a plausible dive aim point.
      designation.copy(BODY_FORWARD).applyQuaternion(orientation).multiplyScalar(900);
      designation.y -= 60;
      frame.designationM = designation;
    } else {
      frame.designationM = null;
    }

    hud.render(frame, camera, camera.camera.quaternion, aircraftPosition);
  };

  drawFrame();
}

const TOGGLE_LABELS: Record<string, string> = {
  gainReadout: 'GAIN READOUT',
  highContrast: 'HIGH CONTRAST',
};

const EMPTY_TAGS: BearingTag[] = [];
const EMPTY_CONTACTS: RwrContact[] = [];
const EMPTY_MISSILES: MissileWarning[] = [];
const INACTIVE_LOOK = { rateX: 0, rateY: 0, deltaXRad: 0, deltaYRad: 0, active: false };

function makeTag(
  designation: string,
  type: string,
  bearingDeg: number,
  kind: (typeof TagKind)[keyof typeof TagKind],
): BearingTag {
  const bearing = bearingDeg * DEG_TO_RAD;
  const range = 8000;
  return {
    designation,
    type,
    positionM: new THREE.Vector3(Math.sin(bearing) * range, 0, -Math.cos(bearing) * range),
    kind,
  };
}

function buildPanel(
  values: Record<SliderKey, number>,
  toggles: Record<string, boolean>,
  hud: Hud,
): void {
  const panel = document.createElement('div');
  panel.setAttribute('style', PANEL_STYLE);

  const title = document.createElement('div');
  title.textContent = 'HUD TEST · ?scene=hudtest';
  title.style.opacity = '0.65';
  title.style.marginBottom = '8px';
  panel.appendChild(title);

  for (const key of Object.keys(SLIDERS) as SliderKey[]) {
    const spec = SLIDERS[key];
    const row = document.createElement('label');
    row.style.display = 'block';
    row.style.marginBottom = '6px';

    const caption = document.createElement('div');
    caption.style.display = 'flex';
    caption.style.justifyContent = 'space-between';
    caption.style.opacity = '0.8';

    const name = document.createElement('span');
    name.textContent = spec.label;
    const readout = document.createElement('span');
    readout.textContent = formatValue(spec, values[key]);
    caption.append(name, readout);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(spec.min);
    slider.max = String(spec.max);
    slider.step = String(spec.step);
    slider.value = String(values[key]);
    slider.style.width = '100%';
    slider.style.accentColor = HUD.colour;
    slider.addEventListener('input', () => {
      values[key] = Number(slider.value);
      readout.textContent = formatValue(spec, values[key]);
    });

    row.append(caption, slider);
    panel.appendChild(row);
  }

  for (const key of ['rwr', 'missiles', 'tags', 'designation', 'gainReadout', 'highContrast']) {
    const row = document.createElement('label');
    row.style.display = 'flex';
    row.style.gap = '6px';
    row.style.alignItems = 'center';
    row.style.marginTop = '4px';

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = toggles[key];
    box.style.accentColor = HUD.colour;
    box.addEventListener('change', () => {
      toggles[key] = box.checked;
      if (key === 'highContrast') hud.setHighContrast(box.checked);
    });

    const name = document.createElement('span');
    name.textContent = TOGGLE_LABELS[key] ?? key;
    row.append(box, name);
    panel.appendChild(row);
  }

  document.body.appendChild(panel);
}

function formatValue(spec: SliderSpec, value: number): string {
  const decimals = spec.step < 1 ? 2 : 0;
  return `${value.toFixed(decimals)}${spec.unit ?? ''}`;
}
